import { daysBetween, parseDate, todayISO } from '@/lib/utils/date'
import type { Book } from '@/types'

/**
 * Pure, client-side analytics over the user's library.
 * Everything here works on a flat list of books so it can be unit-tested and reused
 * by the dashboard, the year-in-review page and the roulette suggestions.
 */

export interface LibraryGenre {
  id: string
  name: string
  color: string
}

export type LibraryBook = Omit<Book, 'book_genres'> & { genres: LibraryGenre[] }

/** Books in the user's library: not wishlist, not borrowed from someone else */
export function ownedBooks(books: LibraryBook[]): LibraryBook[] {
  return books.filter((b) => b.status !== 'wishlist' && b.ownership !== 'borrowed')
}

/** Books marked as read but without an end date (they don't count in yearly stats) */
export function booksMissingEndDate(books: LibraryBook[]): LibraryBook[] {
  return books.filter((b) => b.status === 'read' && !b.end_date)
}

/** Best guess for when a read book was finished: last update, never before start or creation */
export function suggestEndDate(book: LibraryBook): string {
  const candidate = book.updated_at.slice(0, 10)
  if (book.start_date && candidate < book.start_date) return book.start_date
  return candidate > todayISO() ? todayISO() : candidate
}

export interface ReadingBook {
  book: LibraryBook
  /** Days since start_date, null when there is no start date */
  daysReading: number | null
}

/** Books currently being read, the most recently started first */
export function currentlyReading(books: LibraryBook[], today = new Date()): ReadingBook[] {
  return books
    .filter((b) => b.status === 'reading')
    .map((book) => ({
      book,
      daysReading: book.start_date
        ? Math.max(0, daysBetween(parseDate(book.start_date), today))
        : null,
    }))
    .sort((a, b) => (a.daysReading ?? Infinity) - (b.daysReading ?? Infinity))
}

/** Books in "reading" for longer than `thresholdDays`, the oldest first */
export function stalledBooks(
  books: LibraryBook[],
  thresholdDays = 60,
  today = new Date()
): ReadingBook[] {
  return currentlyReading(books, today)
    .filter((r) => r.daysReading !== null && r.daysReading > thresholdDays)
    .reverse()
}

export interface ReadingTimeStats {
  /** Number of books with both dates set */
  sampleSize: number
  averageDays: number
  fastest: { book: LibraryBook; days: number } | null
  slowest: { book: LibraryBook; days: number } | null
}

/** Average days between start_date and end_date for read books */
export function readingTimeStats(books: LibraryBook[]): ReadingTimeStats {
  const durations = books
    .filter((b) => b.status === 'read' && b.start_date && b.end_date)
    .map((book) => ({
      book,
      // A book started and finished on the same day counts as 1 day
      days: Math.max(1, daysBetween(parseDate(book.start_date!), parseDate(book.end_date!)) + 1),
    }))
    .filter((d) => d.days > 0)

  if (durations.length === 0) {
    return { sampleSize: 0, averageDays: 0, fastest: null, slowest: null }
  }

  const sorted = [...durations].sort((a, b) => a.days - b.days)
  const total = durations.reduce((sum, d) => sum + d.days, 0)

  return {
    sampleSize: durations.length,
    averageDays: Math.round(total / durations.length),
    fastest: sorted[0],
    slowest: sorted[sorted.length - 1],
  }
}

export interface AuthorStats {
  author: string
  total: number
  read: number
  averageRating: number | null
}

/** Authors with the most owned books (ties broken by books read, then rating) */
export function topAuthors(books: LibraryBook[], limit = 5): AuthorStats[] {
  const byAuthor = new Map<
    string,
    { name: string; total: number; read: number; ratings: number[] }
  >()

  for (const book of ownedBooks(books)) {
    const name = book.author.trim()
    if (!name) continue
    const key = name.toLocaleLowerCase()
    const entry = byAuthor.get(key) ?? { name, total: 0, read: 0, ratings: [] }
    entry.total++
    if (book.status === 'read') entry.read++
    if (book.rating) entry.ratings.push(book.rating)
    byAuthor.set(key, entry)
  }

  return Array.from(byAuthor.values())
    .map((e) => ({
      author: e.name,
      total: e.total,
      read: e.read,
      averageRating: e.ratings.length
        ? Math.round((e.ratings.reduce((a, b) => a + b, 0) / e.ratings.length) * 10) / 10
        : null,
    }))
    .sort(
      (a, b) =>
        b.total - a.total || b.read - a.read || (b.averageRating ?? 0) - (a.averageRating ?? 0)
    )
    .slice(0, limit)
}

export interface GenreBacklog {
  genre: LibraryGenre
  owned: number
  read: number
  unread: number
}

/** Per genre: how many owned books are still unread, the biggest backlog first */
export function genreBacklog(books: LibraryBook[], limit = 6): GenreBacklog[] {
  const byGenre = new Map<string, GenreBacklog>()

  for (const book of ownedBooks(books)) {
    for (const genre of book.genres) {
      const entry = byGenre.get(genre.id) ?? { genre, owned: 0, read: 0, unread: 0 }
      entry.owned++
      if (book.status === 'read') entry.read++
      else entry.unread++
      byGenre.set(genre.id, entry)
    }
  }

  return Array.from(byGenre.values())
    .sort((a, b) => b.unread - a.unread || b.owned - a.owned)
    .slice(0, limit)
}

export interface MonthlyPace {
  /** YYYY-MM */
  month: string
  added: number
  finished: number
}

/** Books added to the library vs books finished, per month for the last `months` months */
export function monthlyPace(books: LibraryBook[], months = 12, today = new Date()): MonthlyPace[] {
  const buckets: MonthlyPace[] = []
  for (let i = months - 1; i >= 0; i--) {
    const d = new Date(today.getFullYear(), today.getMonth() - i, 1)
    buckets.push({ month: monthKey(d), added: 0, finished: 0 })
  }
  const index = new Map(buckets.map((b, i) => [b.month, i]))

  // Added = books that joined the library; finished = any book read, borrowed ones included
  for (const book of ownedBooks(books)) {
    const added = index.get(monthKey(new Date(book.created_at)))
    if (added !== undefined) buckets[added].added++
  }
  for (const book of books) {
    if (book.status === 'read' && book.end_date) {
      const finished = index.get(book.end_date.slice(0, 7))
      if (finished !== undefined) buckets[finished].finished++
    }
  }

  return buckets
}

function monthKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
}

export interface ReadingStreak {
  /** Consecutive months (up to now) with at least one finished book */
  current: number
  longest: number
  /** True when the current month already has a finished book */
  activeThisMonth: boolean
}

/**
 * Month-based reading streak. The current month doesn't break the streak until it's over,
 * so a streak that ended last month is still "alive".
 */
export function readingStreak(books: LibraryBook[], today = new Date()): ReadingStreak {
  const months = new Set(
    books.filter((b) => b.status === 'read' && b.end_date).map((b) => b.end_date!.slice(0, 7))
  )

  const thisMonth = monthKey(today)
  const activeThisMonth = months.has(thisMonth)

  let current = 0
  const cursor = new Date(today.getFullYear(), today.getMonth() - (activeThisMonth ? 0 : 1), 1)
  while (months.has(monthKey(cursor))) {
    current++
    cursor.setMonth(cursor.getMonth() - 1)
  }

  let longest = 0
  let run = 0
  let previous: Date | null = null
  for (const key of Array.from(months).sort()) {
    const [y, m] = key.split('-').map(Number)
    const date = new Date(y, m - 1, 1)
    const consecutive =
      previous !== null &&
      date.getFullYear() * 12 + date.getMonth() ===
        previous.getFullYear() * 12 + previous.getMonth() + 1
    run = consecutive ? run + 1 : 1
    longest = Math.max(longest, run)
    previous = date
  }

  return { current, longest, activeThisMonth }
}

export interface GenreAffinity {
  genre: LibraryGenre
  preferred: boolean
  owned: number
  read: number
  averageRating: number | null
}

/**
 * Compares the genres the user said they like with what they actually read.
 * Returns preferred genres plus any genre they've read, ordered by books read.
 */
export function preferredVsActual(
  books: LibraryBook[],
  preferred: LibraryGenre[]
): GenreAffinity[] {
  const preferredIds = new Set(preferred.map((g) => g.id))
  const byGenre = new Map<string, GenreAffinity & { ratings: number[] }>()

  for (const genre of preferred) {
    byGenre.set(genre.id, {
      genre,
      preferred: true,
      owned: 0,
      read: 0,
      averageRating: null,
      ratings: [],
    })
  }

  for (const book of books) {
    if (book.status === 'wishlist') continue
    for (const genre of book.genres) {
      const entry = byGenre.get(genre.id) ?? {
        genre,
        preferred: preferredIds.has(genre.id),
        owned: 0,
        read: 0,
        averageRating: null,
        ratings: [],
      }
      if (book.ownership !== 'borrowed') entry.owned++
      if (book.status === 'read') {
        entry.read++
        if (book.rating) entry.ratings.push(book.rating)
      }
      byGenre.set(genre.id, entry)
    }
  }

  return Array.from(byGenre.values())
    .filter((e) => e.preferred || e.read > 0)
    .map(({ ratings, ...e }) => ({
      ...e,
      averageRating: ratings.length
        ? Math.round((ratings.reduce((a, b) => a + b, 0) / ratings.length) * 10) / 10
        : null,
    }))
    .sort((a, b) => b.read - a.read || Number(b.preferred) - Number(a.preferred))
}

export interface YearInReview {
  year: number
  booksRead: LibraryBook[]
  booksAdded: number
  topGenre: { genre: LibraryGenre; count: number } | null
  topAuthor: { author: string; count: number } | null
  bestRated: LibraryBook | null
  fastest: { book: LibraryBook; days: number } | null
  averageRating: number | null
  averageDays: number | null
  busiestMonth: { month: number; count: number } | null
  physical: number
  digital: number
}

/** Summary of a calendar year, based on end_date for finished books */
export function yearInReview(books: LibraryBook[], year: number): YearInReview {
  const prefix = String(year)
  const booksRead = books
    .filter((b) => b.status === 'read' && b.end_date?.startsWith(prefix))
    .sort((a, b) => a.end_date!.localeCompare(b.end_date!))

  const booksAdded = ownedBooks(books).filter((b) => b.created_at.startsWith(prefix)).length

  const genreCounts = new Map<string, { genre: LibraryGenre; count: number }>()
  const authorCounts = new Map<string, { author: string; count: number }>()
  const monthCounts = new Map<number, number>()
  const ratings: number[] = []

  for (const book of booksRead) {
    for (const genre of book.genres) {
      const entry = genreCounts.get(genre.id) ?? { genre, count: 0 }
      entry.count++
      genreCounts.set(genre.id, entry)
    }
    const authorKey = book.author.trim().toLocaleLowerCase()
    const author = authorCounts.get(authorKey) ?? { author: book.author.trim(), count: 0 }
    author.count++
    authorCounts.set(authorKey, author)

    const month = Number(book.end_date!.slice(5, 7))
    monthCounts.set(month, (monthCounts.get(month) ?? 0) + 1)

    if (book.rating) ratings.push(book.rating)
  }

  const maxBy = <T extends { count: number }>(items: Iterable<T>): T | null => {
    let best: T | null = null
    for (const item of items) if (!best || item.count > best.count) best = item
    return best
  }

  const bestRated =
    [...booksRead].filter((b) => b.rating).sort((a, b) => (b.rating ?? 0) - (a.rating ?? 0))[0] ??
    null

  const times = readingTimeStats(booksRead)
  const busiest = maxBy(Array.from(monthCounts, ([month, count]) => ({ month, count })))

  return {
    year,
    booksRead,
    booksAdded,
    topGenre: maxBy(genreCounts.values()),
    topAuthor: maxBy(authorCounts.values()),
    bestRated,
    fastest: times.fastest,
    averageRating: ratings.length
      ? Math.round((ratings.reduce((a, b) => a + b, 0) / ratings.length) * 10) / 10
      : null,
    averageDays: times.sampleSize ? times.averageDays : null,
    busiestMonth: busiest,
    physical: booksRead.filter((b) => b.type === 'physical').length,
    digital: booksRead.filter((b) => b.type === 'digital').length,
  }
}

/** Years that have at least one finished book, most recent first */
export function yearsWithReads(books: LibraryBook[]): number[] {
  const years = new Set(
    books
      .filter((b) => b.status === 'read' && b.end_date)
      .map((b) => Number(b.end_date!.slice(0, 4)))
  )
  return Array.from(years).sort((a, b) => b - a)
}

/**
 * Scores unread/reading books for the roulette: genres and authors the user rated well
 * score higher, preferred genres get a bonus. Returns the top `limit` book ids.
 */
export function suggestRouletteBooks(
  books: LibraryBook[],
  preferredGenreIds: string[],
  limit = 10
): string[] {
  const preferred = new Set(preferredGenreIds)
  const genreRatings = new Map<string, number[]>()
  const authorRatings = new Map<string, number[]>()

  for (const book of books) {
    if (book.status !== 'read') continue
    // Read books without a rating still signal interest; count them as neutral (3)
    const rating = book.rating ?? 3
    for (const genre of book.genres) {
      genreRatings.set(genre.id, [...(genreRatings.get(genre.id) ?? []), rating])
    }
    const author = book.author.trim().toLocaleLowerCase()
    authorRatings.set(author, [...(authorRatings.get(author) ?? []), rating])
  }

  const avg = (values: number[] | undefined) =>
    values?.length ? values.reduce((a, b) => a + b, 0) / values.length : null

  return books
    .filter((b) => b.status === 'unread' || b.status === 'reading')
    .map((book) => {
      const genreScores = book.genres.map((g) => avg(genreRatings.get(g.id)) ?? 2.5)
      const genreScore = genreScores.length ? Math.max(...genreScores) : 2.5
      const preferredBonus = book.genres.some((g) => preferred.has(g.id)) ? 1 : 0
      const authorAvg = avg(authorRatings.get(book.author.trim().toLocaleLowerCase()))
      const authorBonus = authorAvg !== null ? (authorAvg - 3) * 0.75 : 0
      return { id: book.id, score: genreScore + preferredBonus + authorBonus }
    })
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map((s) => s.id)
}
